/**
 * Запись экрана для формы задачи: видео выбранной вкладки + MediaRecorder.
 * Поток — из окна выбора Chrome (displayStream) или от расширения «Запись
 * бага» (tabStream). Звук не пишется — он баг не показывает, а весит.
 *
 * Запись живёт в памяти этой вкладки: закрыть или перезагрузить форму
 * значит потерять запись, поэтому на время записи стоит предупреждение
 * beforeunload. Остановить можно кнопкой в форме или «Закрыть доступ»
 * на панели Chrome — итог один и тот же.
 */

import { MAX_RECORDING_BYTES } from "@taskflow/client-core/tasks";
import { useCallback, useEffect, useRef, useState } from "react";

import { withWebmDuration } from "./webmDuration";

/** 1 Мбит/с — текст интерфейса читается, минута весит около 7,5 МБ. */
const VIDEO_BITS_PER_SECOND = 1_000_000;
/** Запас под последний кусок: запись останавливается раньше предела. */
const LIMIT_MARGIN = 4 * 1024 * 1024;

const MIME_CANDIDATES = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm", "video/mp4"];

export type RecorderState =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "recording"; elapsedMs: number; bytes: number }
  | { kind: "finishing" };

/** Записывать можно только там, где браузер даёт захват экрана: на
 *  телефоне и в незащищённом контексте (http по IP) его нет. */
export function canRecordScreen(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.MediaRecorder === "function" &&
    typeof navigator.mediaDevices?.getDisplayMedia === "function"
  );
}

/** Вкладка из окна выбора Chrome. Подсказки: сразу вкладки, без всего
 *  экрана и без самой формы задачи; «Поделиться этой вкладкой» на панели
 *  переключает запись на другую вкладку без остановки. */
export function displayStream(): Promise<MediaStream> {
  return navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: "browser", frameRate: { ideal: 15, max: 30 } },
    audio: false,
    preferCurrentTab: false,
    selfBrowserSurface: "exclude",
    surfaceSwitching: "include",
    monitorTypeSurfaces: "exclude",
  } as DisplayMediaStreamOptions);
}

/** Вкладка, выданная расширением (chrome.tabCapture.getMediaStreamId):
 *  без окна выбора — вкладку уже указали нажатием на значок расширения. */
export function tabStream(streamId: string): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      mandatory: { chromeMediaSource: "tab", chromeMediaSourceId: streamId, maxFrameRate: 15 },
    },
  } as unknown as MediaStreamConstraints);
}

function pickMime(): string {
  return MIME_CANDIDATES.find((mime) => MediaRecorder.isTypeSupported(mime)) ?? "";
}

function twoDigits(value: number): string {
  return String(value).padStart(2, "0");
}

/** «Запись экрана 22.10.2026 14-05.webm»: двоеточие сервер всё равно заменил бы. */
/** Видео, снятые здесь: при загрузке они помечаются записью бага, и
 *  сервер удалит их при закрытии задачи. Ручное видео так не помечается. */
const recordedVideos = new WeakSet<File>();

export function isRecordedVideo(file: File): boolean {
  return recordedVideos.has(file);
}

export function recordingStamp(at: Date): string {
  const date = `${twoDigits(at.getDate())}.${twoDigits(at.getMonth() + 1)}.${String(at.getFullYear())}`;
  return `${date} ${twoDigits(at.getHours())}-${twoDigits(at.getMinutes())}`;
}

export function recordingFileName(at: Date, mime: string): string {
  return `Запись экрана ${recordingStamp(at)}.${mime.startsWith("video/mp4") ? "mp4" : "webm"}`;
}

interface Session {
  stream: MediaStream;
  recorder: MediaRecorder;
  chunks: Blob[];
  bytes: number;
  startedAt: number;
  startedDate: Date;
  timer: number;
}

export function useScreenRecorder({
  onRecorded,
  onLimitReached,
  onError,
}: {
  onRecorded: (file: File) => void;
  onLimitReached: () => void;
  onError: (message: string) => void;
}) {
  const [state, setState] = useState<RecorderState>({ kind: "idle" });
  const session = useRef<Session | null>(null);
  // Обработчики — через ref: запись переживает перерисовки формы.
  const handlers = useRef({ onRecorded, onLimitReached, onError });
  useEffect(() => {
    handlers.current = { onRecorded, onLimitReached, onError };
  });
  const mounted = useRef(true);

  const stop = useCallback(() => {
    const current = session.current;
    if (current === null || current.recorder.state === "inactive") return;
    setState({ kind: "finishing" });
    current.recorder.stop();
  }, []);

  /** false — запись не началась (отказ в окне выбора или ошибка). */
  const start = useCallback(async (acquire: () => Promise<MediaStream> = displayStream) => {
    if (session.current !== null) return false;
    setState({ kind: "starting" });
    let stream: MediaStream;
    try {
      stream = await acquire();
    } catch (raw) {
      setState({ kind: "idle" });
      // Отказ в окне выбора — не ошибка: человек передумал.
      if (raw instanceof DOMException && raw.name === "NotAllowedError") return false;
      handlers.current.onError(raw instanceof Error ? raw.message : "Захват экрана недоступен");
      return false;
    }

    const mime = pickMime();
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, {
        ...(mime === "" ? {} : { mimeType: mime }),
        videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
      });
    } catch (raw) {
      for (const track of stream.getTracks()) track.stop();
      setState({ kind: "idle" });
      handlers.current.onError(raw instanceof Error ? raw.message : "Запись не поддерживается");
      return false;
    }

    const current: Session = {
      stream,
      recorder,
      chunks: [],
      bytes: 0,
      startedAt: performance.now(),
      startedDate: new Date(),
      timer: 0,
    };
    session.current = current;

    recorder.ondataavailable = (event) => {
      if (event.data.size === 0) return;
      current.chunks.push(event.data);
      current.bytes += event.data.size;
      if (current.bytes >= MAX_RECORDING_BYTES - LIMIT_MARGIN && recorder.state === "recording") {
        handlers.current.onLimitReached();
        stop();
      }
    };
    recorder.onstop = () => {
      window.clearInterval(current.timer);
      for (const track of stream.getTracks()) track.stop();
      session.current = null;
      if (!mounted.current) return;
      const durationMs = performance.now() - current.startedAt;
      const type = (recorder.mimeType || mime || "video/webm").split(";")[0] ?? "video/webm";
      const raw = new Blob(current.chunks, { type });
      void (type === "video/webm" ? withWebmDuration(raw, durationMs) : Promise.resolve(raw)).then(
        (blob) => {
          if (!mounted.current) return;
          setState({ kind: "idle" });
          if (blob.size === 0) {
            handlers.current.onError("Запись пустая");
            return;
          }
          const video = new File([blob], recordingFileName(current.startedDate, type), { type });
          recordedVideos.add(video);
          handlers.current.onRecorded(video);
        },
      );
    };
    // «Закрыть доступ» на панели Chrome завершает дорожку — это та же остановка.
    for (const track of stream.getVideoTracks()) track.addEventListener("ended", stop);

    // Куски раз в секунду: размер виден по ходу, а предел ловится вовремя.
    recorder.start(1000);
    current.timer = window.setInterval(() => {
      setState({
        kind: "recording",
        elapsedMs: performance.now() - current.startedAt,
        bytes: current.bytes,
      });
    }, 500);
    setState({ kind: "recording", elapsedMs: 0, bytes: 0 });
    return true;
  }, [stop]);

  // Пока идёт запись, уход со страницы её уничтожит — браузер переспросит.
  const active = state.kind !== "idle";
  useEffect(() => {
    if (!active) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [active]);

  // Форму закрыли посреди записи — захват экрана отпускается.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      const current = session.current;
      if (current === null) return;
      window.clearInterval(current.timer);
      if (current.recorder.state !== "inactive") current.recorder.stop();
      for (const track of current.stream.getTracks()) track.stop();
      session.current = null;
    };
  }, []);

  return { state, start, stop };
}
